import { useEffect } from 'react';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';
import { testimonialsData } from '../../mocks/testimonials';
import { useSEO } from '../../utils/seo';

const TestimonialsPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useSEO({
    title: 'お客様の声｜岡山市の印刷会社 おかやま印刷株式会社',
    description: 'おかやま印刷株式会社をご利用いただいたお客様の声をご紹介します。卒団記念品、販促物、オリジナルグッズの制作でご満足いただいたお客様の評価をご覧ください。',
    keywords: 'お客様の声,評判,口コミ,レビュー,印刷会社,岡山市,おかやま印刷',
    canonical: '/testimonials',
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: 'お客様の声',
      description: 'おかやま印刷株式会社をご利用いただいたお客様の声をご紹介します。',
      provider: {
        '@type': 'LocalBusiness',
        name: 'おかやま印刷株式会社',
      },
    },
  });

  const allTestimonials = [
    ...testimonialsData,
    {
      id: 5,
      name: '高橋 真由美',
      position: '地元バスケットボールクラブ コーチ',
      comment: '毎年、卒団記念品をお願いしています。子どもたちの成長を見守ってきた私たちにとって、記念品は特別な意味を持ちます。いつも丁寧に対応してくださり、子どもたちも保護者も大変喜んでいます。',
      rating: 5,
      image: 'https://public.readdy.ai/ai/img_res/82361e9bf46b7a88220f82039cd603c3.jpg',
    },
    {
      id: 6,
      name: '中村 健一',
      position: '製造業 総務部',
      comment: '会社の周年記念品を大量に発注しましたが、品質も納期も完璧でした。社員からも好評で、会社のブランディングにも貢献していただきました。今後もお付き合いをお願いしたいと思います。',
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=A%20confident%20Japanese%20businessman%20in%20his%2050s%20wearing%20a%20business%20suit%20with%20a%20professional%20smile%20in%20a%20modern%20office%20environment%20with%20soft%20lighting%20and%20a%20neutral%20clean%20background&width=400&height=400&seq=testimonial006&orientation=squarish',
    },
    {
      id: 7,
      name: '小林 愛',
      position: 'ヨガスタジオオーナー',
      comment: 'スタジオのオリジナルグッズを制作していただきました。デザインの相談から納品まで、とても親身に対応してくださいました。生徒さんたちにも大変喜ばれています。',
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=A%20serene%20Japanese%20woman%20in%20her%2030s%20with%20a%20gentle%20smile%20wearing%20casual%20yoga%20attire%20in%20a%20bright%20peaceful%20studio%20setting%20with%20natural%20lighting%20and%20a%20soft%20clean%20background&width=400&height=400&seq=testimonial007&orientation=squarish',
    },
    {
      id: 8,
      name: '渡辺 修',
      position: '地域イベント企画会社 代表',
      comment: '地域のお祭りやイベントで使用するTシャツやグッズを毎回お願いしています。短納期でも対応してくださり、品質も安定しています。地域に根ざした企業として、これからも応援しています。',
      rating: 5,
      image: 'https://readdy.ai/api/search-image?query=A%20friendly%20Japanese%20man%20in%20his%2040s%20with%20an%20energetic%20smile%20wearing%20casual%20business%20attire%20in%20a%20bright%20community%20event%20space%20with%20natural%20lighting%20and%20a%20clean%20simple%20background&width=400&height=400&seq=testimonial008&orientation=squarish',
    },
  ];

  return (
    <div className="min-h-screen">
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative h-[50vh] min-h-[400px] flex items-center justify-center overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-primary via-[#FF7A30] to-[#E85A20]"></div>
          <div className="relative z-10 text-center px-4">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-4 drop-shadow-lg">
              お客様の声
            </h1>
            <p className="text-xl md:text-2xl lg:text-3xl text-white drop-shadow-md">
              多くのお客様にご満足いただいています
            </p>
          </div>
        </section>

        {/* Testimonials Section */}
        <section className="py-16 md:py-24 bg-white">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto space-y-12 md:space-y-16">
              {allTestimonials.map((testimonial, index) => (
                <article
                  key={testimonial.id}
                  className={`flex flex-col ${
                    index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
                  } gap-8 lg:gap-12 items-center bg-white rounded-3xl p-6 md:p-8 shadow-xl hover:shadow-2xl transition-shadow duration-300`}
                >
                  <div className="flex-shrink-0">
                    <img
                      src={testimonial.image}
                      alt={testimonial.name}
                      className="w-32 h-32 md:w-40 md:h-40 rounded-full object-cover shadow-lg"
                    />
                  </div>
                  <div className="flex-1 text-center lg:text-left">
                    <div className="text-6xl md:text-7xl text-primary/20 leading-none mb-4">"</div>
                    <p className="text-base md:text-lg lg:text-xl text-gray-700 leading-relaxed mb-6">
                      {testimonial.comment}
                    </p>
                    <div className="space-y-2">
                      <p className="font-bold text-lg md:text-xl text-gray-900">
                        {testimonial.name}
                      </p>
                      <p className="text-sm md:text-base text-gray-600">
                        {testimonial.position}
                      </p>
                      <div className="flex items-center justify-center lg:justify-start gap-1 pt-2">
                        {[...Array(testimonial.rating)].map((_, i) => (
                          <i key={i} className="ri-star-fill text-primary text-xl"></i>
                        ))}
                      </div>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        {/* Stats Section */}
        <section className="py-16 md:py-24 bg-gradient-to-br from-primary to-primary-dark">
          <div className="px-4 md:px-8 lg:px-16">
            <div className="max-w-6xl mx-auto">
              <h2 className="text-3xl md:text-4xl font-bold text-white text-center mb-12">
                お客様満足度
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-white mb-2">98%</div>
                  <p className="text-white/90 text-lg">満足度</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-white mb-2">1,200+</div>
                  <p className="text-white/90 text-lg">取引実績</p>
                </div>
                <div className="text-center">
                  <div className="text-5xl md:text-6xl font-bold text-white mb-2">40年</div>
                  <p className="text-white/90 text-lg">創業からの歴史</p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="py-16 md:py-24 relative overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://static.readdy.ai/image/2333ec8ce0f23b8d6e87acea74053a0e/72f95f79575f04d2e5e777734d1956f7.jpeg"
              alt="印刷製品"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-black/50"></div>
          </div>

          <div className="relative z-10 px-4 md:px-8 lg:px-16">
            <div className="max-w-4xl mx-auto text-center">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6">
                あなたも満足いただける仕上がりを
              </h2>
              <p className="text-lg md:text-xl text-white/90 mb-12">
                お見積もりは無料です。お気軽にご相談ください。
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4 md:gap-6">
                <a
                  href="tel:086-012-3456"
                  className="w-full sm:w-auto px-8 py-4 bg-primary text-white rounded-full font-bold text-base md:text-lg hover:bg-primary-dark transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-phone-line text-xl"></i>
                  086-012-3456
                </a>
                <a
                  href="/contact"
                  className="w-full sm:w-auto px-8 py-4 bg-white text-primary border-2 border-primary rounded-full font-bold text-base md:text-lg hover:bg-primary hover:text-white transition-all duration-300 flex items-center justify-center gap-2 whitespace-nowrap cursor-pointer"
                >
                  <i className="ri-mail-line text-xl"></i>
                  お問い合わせフォーム
                </a>
              </div>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TestimonialsPage;
